import { Button } from "@/components/ui/button";
import { Card as UICard, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useRef, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";

import { RulesClient } from "@/lib/rulesClient";
import { parseCardMentions, getCardContext } from "@/lib/cardClient";
import { CardMentionInput } from "@/components/CardMentionInput";
import { CardMentionSpan } from "@/components/CardMentionSpan";
import { CardNameSpan } from "@/components/CardNameSpan";
import { ManaIcon } from "./ManaIcon";

type Message = {
  role: "user" | "model";
  text: string;
};

interface ChatProps {
  rulesClient?: RulesClient;
  rulesLoaded?: boolean;
}

export function Chat({ rulesClient, rulesLoaded }: ChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCards, setSelectedCards] = useState<Scryfall.Card[]>([]);
  // Accumulate all cards seen across the conversation for resolving @{id} in output
  const [cardMap, setCardMap] = useState<Map<string, Scryfall.Card>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // When selectedCards change, update the cardMap
  useEffect(() => {
    setCardMap((prev) => {
      const next = new Map(prev);
      for (const card of selectedCards) {
        next.set(card.id, card);
      }
      return next;
    });
  }, [selectedCards]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", text: input };
    const newMessages = [...messages, userMessage];
    const cardsForThisMessage = [...selectedCards];
    
    setMessages(newMessages);
    setInput("");
    setSelectedCards([]);
    setIsLoading(true);

    try {
      // If user has NOT pre-selected any cards via [[]], fall back to old parsing logic for context
      // Note: We use [[ ]] parsing now in cardClient, so we can try to parse it from the raw text too
      let cardContext = "";
      if (cardsForThisMessage.length === 0) {
        const cardMentions = parseCardMentions(input);
        cardContext = await getCardContext(cardMentions);
        if (cardContext) {
          console.log("Card mentions found:", cardMentions);
        }
      }

      // Client-side RAG: Find relevant rules
      let relevantContext = "";
      if (rulesClient && rulesLoaded) {
        const foundRules = rulesClient.findRelevantRules(input);
        if (foundRules) {
           relevantContext = `Context from MTG Rules:\n${foundRules}\n\n`;
           console.log("Injected rules context length:", foundRules.length);
        }
      }

      // Combine contexts
      const fullContext = cardContext + relevantContext;

      // Transform messages for the API which expects { messages: { role, parts: [{ text }] }[] }
      const history = newMessages.map((msg, idx) => {
        let textToSend = msg.text;
        if (idx === newMessages.length - 1 && msg.role === "user" && fullContext) {
            textToSend = fullContext + "User Question:\n" + msg.text;
        }
        return {
            role: msg.role,
            parts: [{ text: textToSend }]
        };
      });

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: history,
          selectedCards: cardsForThisMessage.length > 0 ? cardsForThisMessage : undefined,
        }),
      });

      const data = await res.json();
      
      if (!res.ok) {
        let errorMessage = data.error || "An unknown error occurred.";
        
        // Handle Rate Limiting
        if (res.status === 429 || errorMessage.includes("429") || errorMessage.includes("Quota exceeded")) {
           errorMessage = "Rate limit exceeded. Please wait a moment before trying again (approx 20s).";
        }
        
        throw new Error(errorMessage);
      }

      const responseText = data.candidates?.[0]?.content?.parts?.[0]?.text || "No response text found";

      setMessages([...newMessages, { role: "model", text: responseText }]);
    } catch (error: any) {
      console.error(error);
      const startErrorMessage = String(error).replace(/^Error: /, "");
      setMessages([...newMessages, { role: "model", text: `⚠️ ${startErrorMessage}` }]);
    } finally {
      setIsLoading(false);
    }
  };

  /** 
   * Process text to replace:
   * 1. @{id} with CardMentionSpan
   * 2. {Symbol} with ManaIcon
   */
  const processText = (text: string): (string | React.ReactNode)[] => {
    // Phase 1: Split by card mentions @{id} or @id (UUIDs)
    // Match @{uuid} or @uuid where uuid is a sequence of hex and dashes
    const cardRegex = /@(?:\{([a-f0-9-]{30,})\}|([a-f0-9-]{30,}))/g;
    const parts1: (string | React.ReactNode)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = cardRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts1.push(text.slice(lastIndex, match.index));
      }
      // match[1] is from @{id}, match[2] is from @id
      const cardId = match[1] || match[2];
      if (cardId) {
        parts1.push(
            <CardMentionSpan key={`card-${cardId}-${match.index}`} cardId={cardId} cardMap={cardMap} />
        );
      }
      lastIndex = cardRegex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts1.push(text.slice(lastIndex));
    }

    // Phase 2: Split by [[Card Name]]
    const parts2: (string | React.ReactNode)[] = [];
    const nameRegex = /\[\[(.*?)\]\]/g;

    for (const part of parts1) {
      if (typeof part !== "string") {
        parts2.push(part);
        continue;
      }

      let innerLastIndex = 0;
      let match;
      while ((match = nameRegex.exec(part)) !== null) {
        if (match.index > innerLastIndex) {
          parts2.push(part.slice(innerLastIndex, match.index));
        }
        const name = match[1];
        if (name) {
            parts2.push(
                <CardNameSpan key={`card-name-${name}-${match.index}`} name={name} cardMap={cardMap} />
            );
        }
        innerLastIndex = nameRegex.lastIndex;
      }
      if (innerLastIndex < part.length) {
        parts2.push(part.slice(innerLastIndex));
      }
    }

    // Phase 3: Process strings for mana symbols {X}
    const manaRegex = /\{([0-9a-zA-Z/]+)\}/g;
    const finalParts: (string | React.ReactNode)[] = [];

    for (const part of parts2) {
      if (typeof part !== "string") {
        finalParts.push(part);
        continue;
      }

      let innerLastIndex = 0;
      let innerMatch;
      // We need to loop over the string part
      while ((innerMatch = manaRegex.exec(part)) !== null) {
          if (innerMatch.index > innerLastIndex) {
              finalParts.push(part.slice(innerLastIndex, innerMatch.index));
          }
          const symbol = innerMatch[1];
          // Simple validation: length <= 3 mostly (e.g. {2/W}), skip long text
          if (symbol && symbol.length <= 5) {
              finalParts.push(
                  <ManaIcon key={`mana-${symbol}-${innerMatch.index}`} symbol={symbol} />
              );
          } else {
              // If it doesn't look like a mana symbol, keep as text
              finalParts.push(innerMatch[0]); 
          }
          innerLastIndex = manaRegex.lastIndex;
      }
      if (innerLastIndex < part.length) {
          finalParts.push(part.slice(innerLastIndex));
      }
    }

    return finalParts;
  };

  // Wrapper for render
  const renderContent = (content: string) => {
      const parts = processText(content);
      return <>{parts}</>;
  };

  // Memoize markdown components config
  const markdownComponents = useMemo(
    () => ({
      // Override text rendering to inject card mention spans & mana icons
      p: ({ children, ...props }: any) => {
        const processed = processChildren(children);
        return <p {...props}>{processed}</p>;
      },
      li: ({ children, ...props }: any) => {
        const processed = processChildren(children);
        return <li {...props}>{processed}</li>;
      },
      strong: ({ children, ...props }: any) => {
        const processed = processChildren(children);
        return <strong {...props}>{processed}</strong>;
      },
      em: ({ children, ...props }: any) => {
        const processed = processChildren(children);
        return <em {...props}>{processed}</em>;
      },
      // Headers
      h1: ({ children, ...props }: any) => <h1 {...props}>{processChildren(children)}</h1>,
      h2: ({ children, ...props }: any) => <h2 {...props}>{processChildren(children)}</h2>,
      h3: ({ children, ...props }: any) => <h3 {...props}>{processChildren(children)}</h3>,
    }),
    [cardMap] // Re-create when cardMap changes
  );

  function processChildren(children: React.ReactNode): React.ReactNode {
    if (typeof children === "string") {
      return renderContent(children);
    }
    if (Array.isArray(children)) {
      return children.map((child, i) => {
        if (typeof child === "string") {
          return <span key={i}>{renderContent(child)}</span>;
        }
        return child;
      });
    }
    return children;
  }

  return (
    <UICard className="w-156  min-w-sm mx-auto min-h-[600px] flex flex-col">
      <CardHeader>
        <CardTitle>MTG Rule Checker</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0 relative ">
        <div className="h-full overflow-y-auto p-4" ref={scrollRef}>
          <div className="flex flex-col gap-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg leading-7 px-4 text-start py-2 ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  }`}
                >
                  <ReactMarkdown components={markdownComponents}>
                    {msg.text}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-4 py-2 animate-pulse">
                  Thinking...
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="p-4">
        <div className="w-full">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex items-center w-full gap-2"
          >
            <CardMentionInput
              value={input}
              onChange={setInput}
              onCardsChange={setSelectedCards}
              selectedCards={selectedCards}
              disabled={isLoading}
              placeholder="Can [[Deflecting Swat]] counter [[Counterspell]]?"
              onSubmit={handleSubmit}
            />
            <Button type="submit" disabled={isLoading}>
              Send
            </Button>
          </form>
          <p className="text-xs italic opacity-50 px-4 mt-2">
            Use [[card name]] to reference cards using <a href="https://scryfall.com/docs/syntax" target="_blank" className="underline">Scryfall syntax</a>.
          </p>
        </div>
      </CardFooter>
    </UICard>
  );
}
